import axios from "axios";
import CryptoJS from "crypto-js";
// Base URLs
const ADMIN_BASE_URL = process.env.REACT_APP_API_URL || "https://admin-api-v1.dequitec.com";
export const WEB_BASE_URL = "https://api-v1.dequitec.com";
const SECRET_KEY = "Nwjy3ghs1d1lrFryuUpWsg==";

// Convert File to Base64
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve({
        filename: file.name,
        contentType: file.type,
        content: reader.result.split(",")[1], // remove data:*/*;base64,
      });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Encrypt standard JS object
const encryptData = (data) => {
  try {
    const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
    const iv = CryptoJS.lib.WordArray.random(16);
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key, { iv });
    return {
      data: encrypted.toString(),
      iv: CryptoJS.enc.Base64.stringify(iv),
    };
  } catch (error) {
    console.error("Encryption Error:", error);
    return data;
  }
};

const isEncryptedPayload = (payload) => {
  if (
    !payload ||
    Object.prototype.toString.call(payload) !== "[object Object]"
  ) {
    return false;
  }
  const { data, iv } = payload;
  return [data, iv].every(
    (value) => Object.prototype.toString.call(value) === "[object String]"
  );
};

// Decrypt backend AES-encrypted response
const decryptData = (encryptedResponse) => {
  if (!isEncryptedPayload(encryptedResponse)) return encryptedResponse;
  try {
    const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
    const iv = CryptoJS.enc.Base64.parse(encryptedResponse.iv);
    const ciphertext = CryptoJS.enc.Base64.parse(encryptedResponse.data);
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: ciphertext },
      key,
      { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );
    return JSON.parse(CryptoJS.enc.Utf8.stringify(decrypted));
  } catch (error) {
    throw new Error("Decryption failed. Please verify encryption keys and IV.");
  }
};

// Encrypt query parameters
const encryptUrlParams = (url) => {
  const [baseUrl, queryString] = url.split("?");
  if (!queryString) return url;

  const params = new URLSearchParams(queryString.trim());
  const encryptedParams = new URLSearchParams();

  params.forEach((value, key) => {
    const encrypted = encryptData(value);
    encryptedParams.append(key, encrypted.data);
    encryptedParams.append(`${key}_iv`, encrypted.iv);
  });

  return `${baseUrl}?${encryptedParams.toString()}`;
};

// Encrypt FormData (files + fields)
const encryptFormData = async (formData) => {
  const plainObj = {};
  const filePromises = [];

  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      filePromises.push(
        fileToBase64(value).then((fileData) => {
          // ✅ Use fileData directly
          if (plainObj[key]) {
            if (!Array.isArray(plainObj[key])) {
              plainObj[key] = [plainObj[key]];
            }
            plainObj[key].push(fileData);
          } else {
            plainObj[key] = [fileData]; // always an array
          }
        })
      );
    } else {
      if (plainObj[key]) {
        if (!Array.isArray(plainObj[key])) {
          plainObj[key] = [plainObj[key]];
        }
        plainObj[key].push(value);
      } else {
        plainObj[key] = value;
      }
    }
  }

  await Promise.all(filePromises);

  const encrypted = encryptData(plainObj);
  const encryptedForm = new FormData();
  encryptedForm.append("data", encrypted.data);
  encryptedForm.append("iv", encrypted.iv);

  return encryptedForm;
};


// Token fetchers
const getToken = () => localStorage.getItem("token");
const getSessionId = () => localStorage.getItem("sessionId");

/**
 * Attach a global response interceptor to handle 403 errors.
 */
const setupAxiosInterceptors = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 403) {
        // Handle unauthorized access here if needed
        // localStorage.clear();
        // window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

// ---------------------------
// Admin Axios Instance Setup
// ---------------------------
const adminInstance = axios.create({
  baseURL: ADMIN_BASE_URL,
  withCredentials: false,
});

adminInstance.interceptors.request.use(
  async(config) => {
    const token = getToken();
    const sessionId = getSessionId();
    const isExternalUrl = typeof config.url === 'string' && /^(http|https):\/\//i.test(config.url);

    if (localStorage.getItem('sessionTimeoutInProgress') === 'true') {
      if (window._tokenCaptureTime && (Date.now() - window._tokenCaptureTime > 10000)) {
        localStorage.removeItem('sessionTimeoutInProgress');
      }
    }

    if (!isExternalUrl && token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      config.data = await encryptFormData(config.data);
    }
    // Encrypt JSON request body
    else if (config.data) {
      config.data = encryptData(config.data);
    }
    
    // Encrypt URL query parameters
    if (config.url?.includes("?")) {
      config.url = encryptUrlParams(config.url);
    }
    if (!isExternalUrl && sessionId) {
      config.headers['X-Session-Id'] = sessionId;
    }
    if (isExternalUrl) {
      config.withCredentials = false;
    }
    return config;
  },
  (error) => Promise.reject(error)
);
adminInstance.interceptors.response.use(
  (response) => {
    if (response.data) {
      response.data = decryptData(response.data);
    }

    return response;
  },
  (error) => Promise.reject(error)
);
setupAxiosInterceptors(adminInstance);

// ---------------------------
// Web Axios Instance Setup
// ---------------------------
const webInstance = axios.create({
  baseURL: WEB_BASE_URL,
  withCredentials: false,
});

webInstance.interceptors.request.use(
  async(config) => {
    const token = getToken();
    const sessionId = getSessionId();
    const isExternalUrl = typeof config.url === 'string' && /^(http|https):\/\//i.test(config.url);

    if (localStorage.getItem('sessionTimeoutInProgress') === 'true') {
      if (window._tokenCaptureTime && (Date.now() - window._tokenCaptureTime > 10000)) {
        localStorage.removeItem('sessionTimeoutInProgress');
      }
    }

    if (!isExternalUrl && token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      config.data = await encryptFormData(config.data);
    }
    // Encrypt JSON request body
    else if (config.data) {
      config.data = encryptData(config.data);
    }

    // Encrypt URL query parameters
    if (config.url?.includes("?")) {
      config.url = encryptUrlParams(config.url);
    }
    if (!isExternalUrl && sessionId) {
      config.headers['X-Session-Id'] = sessionId;
    }
    if (isExternalUrl) {
      config.withCredentials = false;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

webInstance.interceptors.response.use(
  (response) => {
    if (response.data) {
      response.data = decryptData(response.data);
    }

    return response;
  },
  (error) => Promise.reject(error)
);
setupAxiosInterceptors(webInstance);

// Export instances
export { webInstance };
export default adminInstance;



